import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown, Cpu, MonitorSmartphone, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";

const lineAnim = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.16, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const { setAssistantOpen } = useCart();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const yGrid = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section ref={ref} data-testid="hero" className="relative flex min-h-screen items-center overflow-hidden pt-28">
      <motion.div style={{ y: yGrid }} className="tech-grid absolute inset-0" aria-hidden="true" />
      <div className="absolute -left-40 top-1/4 h-[480px] w-[480px] rounded-full bg-voltaic-cyan/10 blur-[140px]" aria-hidden="true" />
      <div className="absolute -right-32 bottom-0 h-[420px] w-[420px] rounded-full bg-voltaic-pink/10 blur-[140px]" aria-hidden="true" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-14 px-5 py-20 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-6 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-voltaic-cyan"
          >
            <span className="h-1.5 w-1.5 animate-pulseDot rounded-full bg-voltaic-cyan" />
            Voltaic // Drop 07 — Now Live
          </motion.p>

          <h1 className="font-display text-5xl font-extrabold uppercase leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl xl:text-8xl">
            <span className="block overflow-hidden pb-1">
              <motion.span custom={0} variants={lineAnim} initial="hidden" animate="show" className="block">
                Built to
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span custom={1} variants={lineAnim} initial="hidden" animate="show" className="text-outline block">
                Electrify
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-6 max-w-md text-base leading-relaxed text-voltaic-muted sm:text-lg"
          >
            Fifteen flagship devices. Zero compromise. Paid securely through Stripe —
            free shipping once you cross $999.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/shop"
              data-testid="hero-cta-shop"
              className="group flex items-center gap-2 rounded-full bg-voltaic-cyan px-7 py-3.5 font-semibold text-voltaic-bg transition-shadow duration-300 hover:glow-cyan"
            >
              Browse the drop
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <button
              data-testid="hero-cta-ai"
              onClick={() => setAssistantOpen(true)}
              className="flex items-center gap-2 rounded-full border border-voltaic-linebright px-7 py-3.5 font-semibold text-voltaic-ink transition-colors duration-300 hover:border-voltaic-cyan hover:text-voltaic-cyan"
            >
              <Zap size={15} /> Ask VOLT AI
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-14 flex items-center gap-8 font-mono text-[10px] uppercase tracking-[0.25em] text-voltaic-muted"
          >
            <span className="flex items-center gap-2"><Cpu size={13} className="text-voltaic-cyan" /> 15 devices</span>
            <span className="h-4 w-px bg-voltaic-line" />
            <span className="flex items-center gap-2"><MonitorSmartphone size={13} className="text-voltaic-cyan" /> 5 categories</span>
            <span className="h-4 w-px bg-voltaic-line" />
            <span>Free ship $999+</span>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            style={{ y: yImg }}
            className="relative mx-auto max-w-sm"
          >
            <div className="absolute inset-0 -z-10 scale-90 rounded-[2.5rem] bg-voltaic-cyan/25 blur-3xl" aria-hidden="true" />
            <div className="overflow-hidden rounded-[2rem] border border-voltaic-linebright shadow-[0_40px_120px_rgba(0,240,255,0.15)]">
              <img
                src="https://images.unsplash.com/photo-1782908423652-2597f19e24eb?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="VOLTAIC CyberPhone Ultra"
                className="aspect-[4/5] w-full object-cover"
              />
            </div>
            <motion.div
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -left-6 top-10 rounded-xl border border-voltaic-line bg-black/70 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-voltaic-cyan backdrop-blur-md"
            >
              165Hz OLED
            </motion.div>
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
              className="absolute -right-4 bottom-14 rounded-xl border border-voltaic-line bg-black/70 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-voltaic-ink backdrop-blur-md"
            >
              Titanium exoskeleton
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-voltaic-muted"
        aria-hidden="true"
      >
        <ChevronDown size={20} />
      </motion.div>
    </section>
  );
}
